import * as React from 'react'
import Cookie from 'js-cookie'
import { useLocale } from 'hooks/useLocale'
import { useUpdateUser } from 'hooks/useUpdateUser'
import { SiteSettingsUpdate } from '../types'
import { useUpdateCartCurrency, suggestLocale } from '.'
import type {
  LacedAvailableCurrencies,
  LacedAvailableLocales,
  LacedAvailableLocations
} from 'types/localisation'

const localeCookieKey = 'preferred_locale'
const currencyCookieKey = 'preferred_currency'
const locationCookieKey = 'preferred_location'

const useSiteSettings = ({
  geoLocale,
  preferredLocale,
  isLoggedIn
}: {
  geoLocale: LacedAvailableLocales
  preferredLocale: LacedAvailableLocales
  isLoggedIn: boolean
}): SiteSettingsHookResult => {
  const { locale, currency, location, localiseUrl } = useLocale()

  const updateCartCurrency = useUpdateCartCurrency()
  const updateUser = useUpdateUser()

  const [isLoading, setIsLoading] = React.useState(false)

  const updateSiteSettings = React.useCallback(
    async (
      selectedLocale: LacedAvailableLocales,
      selectedCurrency: LacedAvailableCurrencies,
      selectedLocation: LacedAvailableLocations
    ): Promise<void> => {
      setIsLoading(true)

      try {
        Cookie.set(currencyCookieKey, selectedCurrency)
        Cookie.set(localeCookieKey, selectedLocale)
        Cookie.set(locationCookieKey, selectedLocation)

        if (isLoggedIn) {
          await updateUser({
            preferredLocale: selectedLocale,
            preferredCurrency: selectedCurrency,
            preferredLocation: selectedLocation
          })
        } else {
          await updateCartCurrency(selectedCurrency, selectedLocation)
        }

        if (selectedLocale !== locale) {
          window.location.href = localiseUrl(window.location.href, {
            localeAsParam: false,
            locale: selectedLocale
          })

          return
        }

        if (
          selectedCurrency !== currency ||
          selectedLocation !== location.code
        ) {
          window.location.reload()
        }
      } catch (err) {
        console.error('error with cart updating user settings', err)
      } finally {
        setIsLoading(false)
      }
    },
    [
      currency,
      isLoggedIn,
      locale,
      localiseUrl,
      location,
      updateCartCurrency,
      updateUser
    ]
  )

  const showSiteSettings = React.useCallback(() => {
    return suggestLocale(geoLocale, locale, preferredLocale)
  }, [geoLocale, locale, preferredLocale])

  return {
    updateSiteSettings,
    showSiteSettings,
    isLoading
  }
}

export default useSiteSettings

interface SiteSettingsHookResult {
  updateSiteSettings: SiteSettingsUpdate
  showSiteSettings: () => boolean
  isLoading: boolean
}
